import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import useAuth from "../../../contexts/AuthContext";
import AlliedApplicationView from "../components/ApplicationView";
import AlliedPrintApplication from "../components/PrintApplication";

const AlliedApplicationDetail: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { apiRequest } = useAuth();

  const [applicationData, setApplicationData] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isPrintView = location.pathname.endsWith("/print");

  useEffect(() => {
    const fetchApplicationDetails = async () => {
      if (!applicationId) {
        setError("No Application ID found in URL.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await apiRequest(`/applications/${applicationId}`);
        setApplicationData(data);
      } catch (err: unknown) {
        const e = err as { message?: string; error?: string };
        setError(e?.message || e?.error || "Failed to fetch application data.");
        console.error("Fetch application error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplicationDetails();
  }, [applicationId, apiRequest]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
        <p className="ml-4 text-lg text-gray-700">Loading Application...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-red-50 text-red-700">
        <AlertCircle className="w-12 h-12 mb-4" />
        <h2 className="text-xl font-bold mb-2">Error Loading Application</h2>
        <p>{error}</p>
        <button
          onClick={() => navigate("/")}
          className="mt-6 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
        >
          Go Home
        </button>
      </div>
    );
  }

  if (applicationData) {
    if (isPrintView) {
      return (
        <AlliedPrintApplication
          formData={applicationData as Record<string, unknown>}
          applicationId={applicationId}
          onBack={() => navigate(-1)}
        />
      );
    }

    return (
      <AlliedApplicationView
        formData={applicationData as Record<string, unknown>}
        applicationId={applicationId}
        onBack={() => navigate(-1)}
      />
    );
  }

  return null;
};

export default AlliedApplicationDetail;
